import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import {
  fetchRemoteAppSettings,
  loadAppSettings,
  mutateSettingOptions,
  persistRemoteAppSettings,
  removeSettingOption,
  settingsLookLikeDefaults,
} from "../config/settings";

const SettingsContext = createContext({
  settings: loadAppSettings(),
  loaded: false,
  refreshSettings: async () => loadAppSettings(),
  upsertOption: async () => loadAppSettings(),
  deleteOption: async () => loadAppSettings(),
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => loadAppSettings());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    function isAdminUser() {
      try {
        const raw = localStorage.getItem('user');
        const parsed = raw ? JSON.parse(raw) : {};
        const roles = Array.isArray(parsed?.roles) ? parsed.roles : parsed?.role ? [parsed.role] : [];
        return roles.map((role) => String(role).toLowerCase()).includes('admin');
      } catch (error) {
        return false;
      }
    }

    async function loadFromServer() {
      const localBeforeFetch = loadAppSettings();
      try {
        const remote = await fetchRemoteAppSettings(api);
        if (cancelled) return;

        if (remote.fromDefaults && isAdminUser() && !settingsLookLikeDefaults(localBeforeFetch)) {
          try {
            const migrated = await persistRemoteAppSettings(api, localBeforeFetch);
            if (!cancelled) setSettings(migrated);
            return;
          } catch (migrateError) {
            console.warn("Could not migrate local dropdown settings to the server", migrateError);
            if (!cancelled) setSettings(localBeforeFetch);
            return;
          }
        }

        setSettings(remote.settings);
      } catch (error) {
        console.warn("Failed to load shared dropdown settings; using local cache", error);
        if (!cancelled) setSettings(loadAppSettings());
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    loadFromServer();
    const onFocus = () => loadFromServer();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  const value = useMemo(
    () => ({
      settings,
      loaded,
      refreshSettings: async () => {
        try {
          const remote = await fetchRemoteAppSettings(api);
          setSettings(remote.settings);
          return remote.settings;
        } catch (error) {
          const local = loadAppSettings();
          setSettings(local);
          return local;
        }
      },
      upsertOption: async (settingKey, value, previousValue = null) => {
        const next = mutateSettingOptions(settings, settingKey, value, previousValue);
        const saved = await persistRemoteAppSettings(api, next);
        setSettings(saved);
        return saved;
      },
      deleteOption: async (settingKey, value) => {
        const next = removeSettingOption(settings, settingKey, value);
        const saved = await persistRemoteAppSettings(api, next);
        setSettings(saved);
        return saved;
      },
    }),
    [settings, loaded]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useAppSettings() {
  return useContext(SettingsContext);
}
